import { resolveTermForMethod } from "@common/constants/domains/accounting/schedules";
import {
    isTermRecognitionMethod,
    RecognitionMethod,
} from "@common/types/domains/accounting/schedule";
import { daysBetween, ScheduleDraft, ScheduleRowDraft } from "./scheduleDraft";
import { offsetDate } from "./scheduleManualApplyHelpers";

type DraftAnchor = "termStart" | "specificDate";

function toAnchor(row: ScheduleRowDraft): DraftAnchor {
    return row.isSpecificDate ? "specificDate" : "termStart";
}

function withRowDates(draft: ScheduleDraft): ScheduleDraft {
    return {
        ...draft,
        rows: draft.rows.map((row) => ({
            ...row,
            date: offsetDate(draft[toAnchor(row)], row.offsetDays),
        })),
    };
}

/** Moves the term start, carrying every row anchored on it along. */
export function moveScheduleDraftTermStart(options: {
    draft: ScheduleDraft;
    termStart: string;
}): ScheduleDraft {
    return withRowDates({ ...options.draft, termStart: options.termStart });
}

export function addScheduleDraftRow(draft: ScheduleDraft): ScheduleDraft {
    return withRowDates({
        ...draft,
        termStart: draft.termStart || draft.specificDate,
        rows: [
            ...draft.rows,
            {
                key: crypto.randomUUID(),
                method: RecognitionMethod.pointInTime,
                amount: 0,
                offsetDays: 0,
                date: "",
                isSpecificDate: false,
            },
        ],
    });
}

/** The over-time row, or else the first row on the anchor, sets it. */
function getAnchorRowKey(options: {
    rows: ScheduleRowDraft[];
    isSpecificDate: boolean;
}): string | undefined {
    const anchoredRows = options.rows.filter(
        (row) => row.isSpecificDate === options.isSpecificDate,
    );
    const termRow = options.isSpecificDate
        ? undefined
        : anchoredRows.find((row) => isTermRecognitionMethod(row.method));
    return (termRow ?? anchoredRows[0])?.key;
}

/**
 * Moving the row that sets its anchor (the term start, or the specific date)
 * moves the anchor, carrying every row on it along. Moving any other row
 * changes only its offset, or sets the anchor while it is still unset.
 */
export function moveScheduleDraftRowDate(options: {
    draft: ScheduleDraft;
    key: string;
    date: string;
}): ScheduleDraft {
    const { draft, key, date } = options;
    const movedRow = draft.rows.find((row) => row.key === key);
    if (!movedRow || !date) {
        return draft;
    }
    const anchor = toAnchor(movedRow);
    const anchorDate = draft[anchor];
    const isAnchorRow =
        getAnchorRowKey({
            rows: draft.rows,
            isSpecificDate: movedRow.isSpecificDate,
        }) === key;
    if (isAnchorRow || !anchorDate) {
        return withRowDates({
            ...draft,
            [anchor]: offsetDate(date, -movedRow.offsetDays),
        });
    }
    return withRowDates({
        ...draft,
        rows: draft.rows.map((row) =>
            row.key === key
                ? { ...row, offsetDays: daysBetween(anchorDate, date) }
                : row,
        ),
    });
}

export function updateScheduleDraftRow(options: {
    draft: ScheduleDraft;
    key: string;
    patch: Partial<Pick<ScheduleRowDraft, "amount" | "method">>;
}): ScheduleDraft {
    const term = resolveTermForMethod({
        method: options.patch.method,
        term: {
            length: options.draft.termLength,
            unit: options.draft.termUnit,
        },
    });
    return {
        ...options.draft,
        termLength: term.length,
        termUnit: term.unit,
        rows: options.draft.rows.map((row) =>
            row.key === options.key ? { ...row, ...options.patch } : row,
        ),
    };
}

export function removeScheduleDraftRow(options: {
    draft: ScheduleDraft;
    key: string;
}): ScheduleDraft {
    return {
        ...options.draft,
        rows: options.draft.rows.filter((row) => row.key !== options.key),
    };
}
